import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import StockSearch from "@/components/StockSearch";
import { GitCompareArrows, X, Sparkles, LogIn, Trophy, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const scoreColor = (s) => (s == null ? "#64748B" : s >= 70 ? "#10B981" : s >= 50 ? "#EAB308" : "#F43F5E");

const Slot = ({ label, code, onPick, onClear }) => (
  <div className="flex-1 min-w-0">
    <p className="text-[11px] text-[var(--text-muted)] uppercase tracking-wider mb-1.5">{label}</p>
    {code ? (
      <div className="flex items-center gap-2 h-14 px-3 rounded-xl bg-[var(--surface-card)] border border-[var(--accent)]/40" data-testid={`compare-slot-${label}`}>
        <span className="w-9 h-9 rounded-lg bg-[var(--accent)]/12 border border-[var(--accent)]/25 flex items-center justify-center text-xs font-bold text-accent shrink-0">{code.slice(0, 2)}</span>
        <p className="font-heading font-bold text-accent flex-1">{code}</p>
        <button onClick={onClear} data-testid={`compare-clear-${label}`} className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-muted)]"><X size={16} /></button>
      </div>
    ) : (
      <StockSearch onSelect={onPick} />
    )}
  </div>
);

const ScoreRow = ({ label, a, b, aStatus, bStatus }) => {
  const aw = a != null && b != null && a > b;
  const bw = a != null && b != null && b > a;
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-3 border-b border-[var(--border-subtle)] last:border-0" data-testid={`compare-row-${label}`}>
      <div className={`flex items-center justify-end gap-2 ${aw ? "opacity-100" : "opacity-80"}`}>
        {aStatus && <span className="hidden sm:inline text-[10px] font-semibold" style={{ color: scoreColor(a) }}>{aStatus}</span>}
        <div className="w-24 sm:w-32">
          <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full rounded-full float-right" style={{ width: `${a || 0}%`, backgroundColor: scoreColor(a) }} /></div>
        </div>
        <span className="font-mono-data font-extrabold text-lg w-8 text-right flex items-center gap-1 justify-end" style={{ color: scoreColor(a) }}>{aw && <Trophy size={12} className="text-gold" />}{a ?? "-"}</span>
      </div>
      <span className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider text-center w-24 sm:w-32">{label}</span>
      <div className={`flex items-center gap-2 ${bw ? "opacity-100" : "opacity-80"}`}>
        <span className="font-mono-data font-extrabold text-lg w-8 flex items-center gap-1" style={{ color: scoreColor(b) }}>{b ?? "-"}{bw && <Trophy size={12} className="text-gold" />}</span>
        <div className="w-24 sm:w-32">
          <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${b || 0}%`, backgroundColor: scoreColor(b) }} /></div>
        </div>
        {bStatus && <span className="hidden sm:inline text-[10px] font-semibold" style={{ color: scoreColor(b) }}>{bStatus}</span>}
      </div>
    </div>
  );
};

const TextRow = ({ label, a, b }) => (
  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-3 border-b border-[var(--border-subtle)] last:border-0">
    <p className="text-sm font-semibold text-right">{a ?? "-"}</p>
    <span className="text-[11px] font-semibold text-[var(--text-muted)] uppercase tracking-wider text-center w-24 sm:w-32">{label}</span>
    <p className="text-sm font-semibold">{b ?? "-"}</p>
  </div>
);

const Head = ({ d }) => {
  if (!d) return <div className="text-center text-[var(--text-muted)] text-sm">-</div>;
  const up = (d.change_pct ?? 0) >= 0;
  return (
    <div className="text-center">
      <p className="font-heading text-2xl font-extrabold">{d.symbol}</p>
      <p className="text-xs text-[var(--text-secondary)] truncate">{d.name}</p>
      <p className={`font-heading text-lg font-extrabold mt-1 ${up ? "text-bull" : "text-bear"}`}>{d.price != null ? `Rp ${d.price.toLocaleString("id-ID")}` : "-"} <span className="text-xs">{up ? "▲" : "▼"} {d.change_pct}%</span></p>
      <div className="mx-auto mt-2 w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: scoreColor(d.overall_score) }}>
        <span className="font-heading text-3xl font-extrabold text-[#07090E]">{d.overall_score}</span>
      </div>
      <p className="text-[10px] text-[var(--text-muted)] mt-1">Overall Score</p>
    </div>
  );
};

export default function CompareSection() {
  const { user, login } = useAuth();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [da, setDa] = useState(null);
  const [dbb, setDbb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    if (!user) { login(); return; }
    if (!a || !b) { setErr("Pilih dua saham terlebih dahulu."); return; }
    if (a === b) { setErr("Pilih dua saham yang berbeda."); return; }
    setErr(""); setLoading(true); setDa(null); setDbb(null);
    try {
      const [ra, rb] = await Promise.all([api.get(`/analysis/${a}`), api.get(`/analysis/${b}`)]);
      setDa(ra.data); setDbb(rb.data);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Masuk untuk melihat perbandingan." : "Gagal memuat analisis. Coba lagi.");
    } finally { setLoading(false); }
  };

  const reset = () => { setDa(null); setDbb(null); setErr(""); };

  return (
    <section id="compare" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Head-to-Head · AI Score"
        title="Bandingkan 2 Saham"
        desc="Sandingkan skor AI dua emiten (Overall, Bandarmology, Technical, Fundamental, News) untuk memutuskan mana yang lebih menarik."
        icon={GitCompareArrows}
      />
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <Slot label="Saham A" code={a} onPick={(c) => { setA(c); reset(); }} onClear={() => { setA(""); reset(); }} />
          <div className="flex items-center justify-center pb-3 sm:pb-3">
            <span className="w-9 h-9 rounded-full bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-center text-xs font-bold text-[var(--text-secondary)]">VS</span>
          </div>
          <Slot label="Saham B" code={b} onPick={(c) => { setB(c); reset(); }} onClear={() => { setB(""); reset(); }} />
          <button onClick={run} disabled={loading} data-testid="compare-run-btn" className="h-14 px-6 rounded-xl bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 shrink-0">
            {loading ? "Menganalisis..." : <>Bandingkan <ArrowRight size={17} /></>}
          </button>
        </div>
        {err && <p className="text-sm text-bear mt-3" data-testid="compare-error">{err}</p>}

        {!user && !err && (
          <div className="mt-6 text-center py-8" data-testid="compare-login-prompt">
            <Sparkles size={26} className="text-accent mx-auto mb-2" />
            <p className="text-sm text-[var(--text-secondary)] mb-4">Masuk untuk membuka perbandingan skor AI antar saham.</p>
            <button onClick={login} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]"><LogIn size={16} /> Masuk dengan Google</button>
          </div>
        )}

        {loading && (
          <div className="mt-8 text-center py-8">
            <div className="w-9 h-9 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-[var(--text-secondary)]">AI membandingkan {a} vs {b}... (±15 detik)</p>
          </div>
        )}

        {da && dbb && !loading && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="compare-result">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-4 pb-5 border-b border-[var(--border)]">
              <Head d={da} />
              <span className="text-sm font-heading font-bold text-[var(--text-muted)]">VS</span>
              <Head d={dbb} />
            </div>
            <ScoreRow label="Overall" a={da.overall_score} b={dbb.overall_score} />
            <ScoreRow label="Bandarmology" a={da.bandarmology?.score} b={dbb.bandarmology?.score} aStatus={da.bandarmology?.status} bStatus={dbb.bandarmology?.status} />
            <ScoreRow label="Technical" a={da.technical?.score} b={dbb.technical?.score} aStatus={da.technical?.status} bStatus={dbb.technical?.status} />
            <ScoreRow label="Fundamental" a={da.fundamental?.score} b={dbb.fundamental?.score} aStatus={da.fundamental?.status} bStatus={dbb.fundamental?.status} />
            <ScoreRow label="News" a={da.news_sentiment?.score} b={dbb.news_sentiment?.score} aStatus={da.news_sentiment?.status} bStatus={dbb.news_sentiment?.status} />
            <ScoreRow label="Valuation" a={da.fundamental?.valuation} b={dbb.fundamental?.valuation} />
            <ScoreRow label="Growth" a={da.fundamental?.growth} b={dbb.fundamental?.growth} />
            <ScoreRow label="Profitability" a={da.fundamental?.profitability} b={dbb.fundamental?.profitability} />
            <ScoreRow label="Health" a={da.fundamental?.health} b={dbb.fundamental?.health} />
            <TextRow label="Fair Value" a={da.fundamental?.fair_value?.verdict} b={dbb.fundamental?.fair_value?.verdict} />
            <TextRow label="Sektor" a={da.sector} b={dbb.sector} />

            <div className="mt-5 p-4 rounded-xl bg-[var(--surface-base)] border border-[var(--border-subtle)] text-center" data-testid="compare-verdict">
              {da.overall_score === dbb.overall_score ? (
                <p className="text-sm">Skor overall <span className="font-bold text-gold">imbang</span> ({da.overall_score}). Perhatikan pilar spesifik sesuai gaya trading Anda.</p>
              ) : (
                <p className="text-sm">Secara overall, <span className="font-heading font-bold text-accent">{(da.overall_score > dbb.overall_score ? da.symbol : dbb.symbol)}</span> unggul dengan skor <span className="font-bold" style={{ color: scoreColor(Math.max(da.overall_score, dbb.overall_score)) }}>{Math.max(da.overall_score, dbb.overall_score)}</span> vs {Math.min(da.overall_score, dbb.overall_score)}.</p>
              )}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] text-center mt-3">Skor dihasilkan AI berbasis data teknikal live + estimasi. Edukasi, bukan ajakan jual/beli. DYOR.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
